import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldTypes } from '@common-fe/common-fe';

import { VALIDATORS } from '@/common';
import { DefaultValues } from '@/common/types';
import { InvestmentExperience, InvestmentObjectives } from '@/modules/investments/investments.types';
import { useFieldsWithDefaultValues } from '@/utils';

interface Params {
  defaultValues: DefaultValues;
}
const useInvestmentsProfileQuestionnaireFields = ({ defaultValues }: Params) => {
  const { t } = useTranslation();
  const INVESTMENT_EXPERIENCE_TYPE_OPTIONS = useMemo(
    () => [
      {
        label: t('I have no experience'),
        value: InvestmentExperience.NONE,
      },
      {
        label: t('Beginner'),
        value: InvestmentExperience.YRS_1_2,
      },
      {
        label: t('Intermediate'),
        value: InvestmentExperience.YRS_3_5,
      },
      {
        label: t('Advanced'),
        value: InvestmentExperience.YRS_5_10,
      },
      {
        label: t('Expert'),
        value: InvestmentExperience.YRS_10,
      },
    ],
    [t]
  );
  const INVESTMENT_OBJECTIVES_TYPE_OPTIONS = useMemo(
    () => [
      {
        label: t('I prefer to minimize fluctuations in my portfolio but want some limited growth potential.'),
        value: InvestmentObjectives.NEW,
      },
      {
        label: t(
          'I am willing to accept lower fluctuations in my portfolio with the opportunity to reach a modest return.'
        ),
        value: InvestmentObjectives.INFREQUENT,
      },
      {
        label: t(
          'I am willing to accept moderate fluctuations in my portfolio for an opportunity for moderate long-term growth.'
        ),
        value: InvestmentObjectives.FREQUENT,
      },
      {
        label: t(
          'I am willing to accept larger fluctuations in my portfolio for an opportunity to earn stronger long-term returns.'
        ),
        value: InvestmentObjectives.ACTIVE_DAILY,
      },
      {
        label: t(
          'I am willing to accept substantial fluctuations in my portfolio for an opportunity to earn significant long-term returns.'
        ),
        value: InvestmentObjectives.LONG_TERM,
      },
    ],
    [t]
  );
  const fields: Field[] = useMemo(() => {
    return [
      {
        name: 'investmentExperienceType',
        type: FieldTypes.Radio,
        showRequireIcon: true,
        fill: true,
        label: t('How would you best describe your experience in Investments?'),
        placeholder: '',
        options: INVESTMENT_EXPERIENCE_TYPE_OPTIONS,
        isWrappedMode: true,
        validator: VALIDATORS.REQUIRED_STRING,
      },
      {
        name: 'investmentObjectivesType',
        type: FieldTypes.Radio,
        showRequireIcon: true,
        fill: true,
        label: t('What is your primary goal for this portfolio?'),
        placeholder: '',
        options: INVESTMENT_OBJECTIVES_TYPE_OPTIONS,
        isWrappedMode: true,
        validator: VALIDATORS.REQUIRED_STRING,
        inputStyle: {
          width: 'auto',
        },
      },
    ];
  }, [INVESTMENT_EXPERIENCE_TYPE_OPTIONS, INVESTMENT_OBJECTIVES_TYPE_OPTIONS, t]);
  const formattedFields = useFieldsWithDefaultValues(fields, defaultValues);
  return formattedFields;
};
export default useInvestmentsProfileQuestionnaireFields;
